import React from "react";
import { Billboard, Line } from "@react-three/drei";
import { ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { SimpleText } from "../../../components/Text/SimpleText";
import {
  editorStore as editorStoreSingleton,
  editorStoreContext,
} from "../../../store/editorStore";
import { FloorplannerStoreContext } from "../../../store/floorplannerStore";
import { renderStore } from "../../../store/renderStore";
import {
  Ruler3DType,
  RulerLineType,
  WallType,
} from "../../../types/floorplannerTypes";
import {
  Measurement3DSpec,
  createRuler3DMeasurement3D,
  createRulerMeasurement3D,
  createWallHeightMeasurement3D,
  createWallLengthMeasurement3D,
  getRuler3DActiveColor,
  getRuler3DOrientationLabel,
  lockRuler3DEndpointToWorldAxis,
  snapRuler3DEndpointToAxisDirections,
  snapRulerEndpointTo45Degrees,
} from "./measurement3DGeometry";
import {
  getMeasurement3DLabelScale,
  getWorldUnitsPerPixelAtPoint,
} from "./measurement3DLabelScale";
import {
  resolveRuler3DPlacement,
  snapRuler3DTranslation,
} from "./measurement3DSnapping";
import { CANVAS_ACCENT_COLOR, CANVAS_ACCENT_COLOR_HEX } from "../../../constants/brandColors";
import { disablePointerRaycast } from "../interaction/disablePointerRaycast";

const LABEL_BACKGROUND_COLOR = 0xfaf8f2;
const SELECTED_LABEL_BACKGROUND_COLOR = 0xe5eaff;
const LABEL_TEXT_COLOR = "#222222";
const LABEL_FONT_SIZE = 14;
const LABEL_HEIGHT = 0.22;
const LABEL_MIN_WIDTH = 0.42;
const LABEL_CHAR_WIDTH = 0.065;
const LABEL_MAX_SCREEN_HEIGHT_PX = 20;
const LABEL_MIN_CAMERA_SCALE = 0.04;
const LABEL_CAMERA_SCALE_OPTIONS = {
  worldHeight: LABEL_HEIGHT,
  maxScreenHeightPx: LABEL_MAX_SCREEN_HEIGHT_PX,
  minScale: LABEL_MIN_CAMERA_SCALE,
  maxScale: 1,
};
const MEASUREMENT_LINE_WIDTH = 0.5;
const RULER_LINE_WIDTH = 0.5;
const DRAG_MOVE_THRESHOLD_PX = 2;
const FLOOR_PLANE = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
const RULER_BODY_HIT_RADIUS_PX = 9;
const RULER_BODY_HIT_FALLBACK_RADIUS = 0.14;
const RULER_HANDLE_RADIUS_PX = 5;
const RULER_HANDLE_HIT_RADIUS_PX = 16;
const RULER_HANDLE_FALLBACK_WORLD_UNITS_PER_PIXEL = 0.011;
/**
 * Measurements are drawn on top of the model but their hit targets sit inside
 * it, so a ruler run along a wall or a symbol would otherwise be shadowed by
 * that surface and become impossible to click. Reporting a slightly shorter
 * ray distance puts the measurement first in the pick order.
 */
const RULER_PICK_BIAS = 1000;

/**
 * Hover cursors must not fight the measurement tool's crosshair, otherwise the
 * pointer flickers back to the default as soon as it passes over a ruler.
 */
function setMeasurementHoverCursor(element: HTMLElement, cursor: string): void {
  if (
    editorStoreSingleton.ruler3DConstructionMode ||
    editorStoreSingleton.rulerConstructionMode
  ) {
    return;
  }
  element.style.cursor = cursor;
  document.body.style.cursor = cursor;
}

function rulerPickRaycast(
  this: THREE.Mesh,
  raycaster: THREE.Raycaster,
  intersects: THREE.Intersection[],
): void {
  const own: THREE.Intersection[] = [];
  THREE.Mesh.prototype.raycast.call(this, raycaster, own);
  own.forEach((intersection) => {
    intersects.push({
      ...intersection,
      distance: intersection.distance - RULER_PICK_BIAS,
    });
  });
}

function rulerHandlePickRaycast(
  this: THREE.Mesh,
  raycaster: THREE.Raycaster,
  intersects: THREE.Intersection[],
): void {
  const own: THREE.Intersection[] = [];
  THREE.Mesh.prototype.raycast.call(this, raycaster, own);
  own.forEach((intersection) => {
    intersects.push({
      ...intersection,
      distance: intersection.distance - RULER_PICK_BIAS - 1,
    });
  });
}

type Ruler3DDragMode = "move" | "start" | "end";
type FreeRuler3DDragMode = "move" | "start" | "end";

type Ruler3DDragState = {
  pointerId: number;
  clientX: number;
  clientY: number;
  pointerTarget?: {
    releasePointerCapture?: (pointerId: number) => void;
  };
  mode: Ruler3DDragMode;
  startPoint: THREE.Vector3;
  startStart: THREE.Vector2;
  startEnd: THREE.Vector2;
  dragging: boolean;
};

type FreeRuler3DDragState = {
  pointerId: number;
  clientX: number;
  clientY: number;
  pointerTarget?: {
    releasePointerCapture?: (pointerId: number) => void;
  };
  mode: FreeRuler3DDragMode;
  dragPlane: THREE.Plane;
  startPoint: THREE.Vector3;
  startStart: THREE.Vector3;
  startEnd: THREE.Vector3;
  dragging: boolean;
};

type Measurement3DLayerProps = {
  walls: WallType[];
  rulerLines: RulerLineType[];
  rulers3D: Ruler3DType[];
  selectedIds: Set<string>;
  onDragActiveChange?: (active: boolean) => void;
};

const Measurement3DLayer: React.FC<Measurement3DLayerProps> = observer(
  ({ walls, rulerLines, rulers3D, selectedIds, onDragActiveChange }) => {
    const editorStore = React.useContext(editorStoreContext);
    const floorplannerStore = React.useContext(FloorplannerStoreContext);

    if (!editorStore.shouldShowMeasurements()) {
      return null;
    }

    const measurements: Measurement3DSpec[] = [];

    walls.forEach((wall) => {
      if (wall.hideMeasurement) return;
      if (editorStore.isWallHidden(wall.id)) return;

      const leftExtent = floorplannerStore.getOutlineSideExtents(wall, "left");
      const rightExtent = floorplannerStore.getOutlineSideExtents(
        wall,
        "right",
      );
      const side = floorplannerStore.getMeasurementDisplaySide(
        wall,
        leftExtent,
        rightExtent,
      );
      const wallWidth = wall.wallWidth ?? floorplannerStore.wallWidth;
      const lengthMeasurement = createWallLengthMeasurement3D(wall, {
        side,
        wallWidth,
        lengthUnit: editorStore.displayLengthUnit,
      });
      if (lengthMeasurement) measurements.push(lengthMeasurement);

      if (selectedIds.has(wall.id)) {
        const heightMeasurement = createWallHeightMeasurement3D(wall, {
          side,
          wallWidth,
          lengthUnit: editorStore.displayLengthUnit,
        });
        if (heightMeasurement) measurements.push(heightMeasurement);
      }
    });

    const visibleRulers = rulerLines.filter((ruler) => !ruler.hideMeasurement);
    const visibleRulers3D = rulers3D.filter((ruler) => !ruler.hideMeasurement);

    if (
      measurements.length === 0 &&
      visibleRulers.length === 0 &&
      visibleRulers3D.length === 0
    )
      return null;

    return (
      <group userData={{ floorplannerType: "measurement-3d-layer" }}>
        {measurements.map((measurement) => (
          <Measurement3DObject
            key={measurement.id}
            measurement={measurement}
            selected={selectedIds.has(measurement.id.split(":")[0])}
          />
        ))}
        {visibleRulers.map((ruler) => (
          <RulerMeasurement3DObject
            key={ruler.id}
            ruler={ruler}
            selected={selectedIds.has(ruler.id)}
            onDragActiveChange={onDragActiveChange}
          />
        ))}
        {visibleRulers3D.map((ruler) => (
          <Ruler3DMeasurementObject
            key={ruler.id}
            ruler={ruler}
            selected={selectedIds.has(ruler.id)}
            onDragActiveChange={onDragActiveChange}
          />
        ))}
      </group>
    );
  },
);

export const Measurement3DObject: React.FC<{
  measurement: Measurement3DSpec;
  selected: boolean;
}> = ({ measurement, selected }) => {
  const { camera, size } = useThree();
  const labelRef = React.useRef<THREE.Group>(null);
  const labelWidth = Math.max(
    LABEL_MIN_WIDTH,
    measurement.label.length * LABEL_CHAR_WIDTH,
  );
  const labelBackground = selected
    ? SELECTED_LABEL_BACKGROUND_COLOR
    : renderStore.labelBackgroundColor || LABEL_BACKGROUND_COLOR;
  const initialLabelScale = getMeasurement3DLabelScale(
    camera,
    measurement.labelPosition,
    size.height,
    LABEL_CAMERA_SCALE_OPTIONS,
  );

  useFrame(({ camera: frameCamera, size: frameSize }) => {
    const label = labelRef.current;
    if (!label) return;

    const scale = getMeasurement3DLabelScale(
      frameCamera,
      measurement.labelPosition,
      frameSize.height,
      LABEL_CAMERA_SCALE_OPTIONS,
    );

    if (Math.abs(label.scale.x - scale) > 1e-4) {
      label.scale.setScalar(scale);
    }
  });

  return (
    <group
      userData={{
        floorplannerType: "measurement-3d",
        floorplannerId: measurement.id,
      }}
    >
      <Line
        points={measurement.points}
        color={measurement.color}
        lineWidth={
          measurement.kind === "ruler-line" || measurement.kind === "ruler-3d"
            ? RULER_LINE_WIDTH
            : MEASUREMENT_LINE_WIDTH
        }
        depthTest={false}
        renderOrder={998}
        raycast={disablePointerRaycast}
      />
      {measurement.capSegments.map((segment, index) => (
        <Line
          key={`${measurement.id}:cap:${index}`}
          points={segment}
          color={measurement.color}
          lineWidth={
            measurement.kind === "ruler-line" || measurement.kind === "ruler-3d"
              ? RULER_LINE_WIDTH
              : MEASUREMENT_LINE_WIDTH
          }
          depthTest={false}
          renderOrder={998}
          raycast={disablePointerRaycast}
        />
      ))}
      <Billboard
        ref={labelRef}
        position={measurement.labelPosition}
        scale={initialLabelScale}
        follow
        lockX={false}
        lockY={false}
        lockZ={false}
      >
        <mesh renderOrder={999} raycast={disablePointerRaycast}>
          <planeGeometry args={[labelWidth, LABEL_HEIGHT]} />
          <meshBasicMaterial
            color={labelBackground}
            depthTest={false}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
        <SimpleText
          position={[0, 0, 0.01]}
          fontSize={LABEL_FONT_SIZE}
          color={LABEL_TEXT_COLOR}
          renderOrder={1000}
          depthTest={false}
          raycast={disablePointerRaycast}
        >
          {measurement.label}
        </SimpleText>
      </Billboard>
    </group>
  );
};

const RulerMeasurement3DObject: React.FC<{
  ruler: RulerLineType;
  selected: boolean;
  onDragActiveChange?: (active: boolean) => void;
}> = observer(({ ruler, selected, onDragActiveChange }) => {
  const editorStore = React.useContext(editorStoreContext);
  const floorplannerStore = React.useContext(FloorplannerStoreContext);
  const { camera, gl, invalidate } = useThree();
  const dragStateRef = React.useRef<Ruler3DDragState | null>(null);
  const measurement = createRulerMeasurement3D(ruler, {
    color: getLineColor(ruler),
    lengthUnit: editorStore.displayLengthUnit,
  });

  const selectRuler = React.useCallback(
    (event: ThreeEvent<PointerEvent> | PointerEvent) => {
      if (
        !event.shiftKey &&
        editorStore.selections.find(
          (selection) => selection.id === ruler.id,
        ) === undefined
      ) {
        editorStore.clearSelections();
      }
      floorplannerStore.selectRuler(ruler.id);
      editorStore.addSelection(ruler);
      invalidate();
    },
    [editorStore, floorplannerStore, invalidate, ruler],
  );

  const applyRulerUpdate = React.useCallback(
    (start: THREE.Vector2, end: THREE.Vector2) => {
      const latestRuler = floorplannerStore.rulerLinesMap.get(ruler.id);
      if (!latestRuler) return;
      floorplannerStore.updateRulerLine({
        ...latestRuler,
        start,
        end,
      });
      invalidate();
    },
    [floorplannerStore, invalidate, ruler.id],
  );

  const finishDrag = React.useCallback(
    (event?: ThreeEvent<PointerEvent> | PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      if (event && event.pointerId !== dragState.pointerId) return;

      stopOrbitControlEvent(event);
      releasePointerCapture(dragState);
      dragStateRef.current = null;
      editorStore.setRulerDragging(null);
      setMeasurementHoverCursor(gl.domElement, "default");

      if (dragState.dragging) {
        const latestRuler = floorplannerStore.rulerLinesMap.get(ruler.id);
        const moved =
          latestRuler &&
          (latestRuler.start.distanceToSquared(dragState.startStart) > 1e-10 ||
            latestRuler.end.distanceToSquared(dragState.startEnd) > 1e-10);

        if (moved) {
          floorplannerStore.recordRulerChange(ruler.id, "modify");
          floorplannerStore.pushToUndoStack();
          floorplannerStore.setDirty();
        } else {
          floorplannerStore.cancelUndoTransaction();
        }
        onDragActiveChange?.(false);
      }
      invalidate();
    },
    [
      editorStore,
      floorplannerStore,
      gl.domElement,
      invalidate,
      onDragActiveChange,
      ruler.id,
    ],
  );

  const moveDrag = React.useCallback(
    (
      event: ThreeEvent<PointerEvent> | PointerEvent,
      currentPoint: THREE.Vector3 | null,
    ) => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) return;

      stopOrbitControlEvent(event);

      if (!dragState.dragging) {
        const movedFarEnough =
          Math.abs(event.clientX - dragState.clientX) >
            DRAG_MOVE_THRESHOLD_PX ||
          Math.abs(event.clientY - dragState.clientY) > DRAG_MOVE_THRESHOLD_PX;

        if (!movedFarEnough) return;

        dragState.dragging = true;
        setMeasurementHoverCursor(
          gl.domElement,
          dragState.mode === "move" ? "grabbing" : "crosshair",
        );
        editorStore.setRulerDragging(ruler.id);
        floorplannerStore.beginUndoTransaction(
          dragState.mode === "move" ? "drag 3D ruler" : "resize 3D ruler",
        );
        floorplannerStore.recordRulerForUndo(ruler.id);
        onDragActiveChange?.(true);
      }

      if (!currentPoint) return;

      const delta = new THREE.Vector2(
        currentPoint.x - dragState.startPoint.x,
        currentPoint.y - dragState.startPoint.y,
      );
      const angleSnapTolerance = editorStore.interactionSnappingTolerance(0.2);
      const nextStart = (() => {
        if (dragState.mode === "end") return dragState.startStart.clone();
        const endpoint = dragState.startStart.clone().add(delta);
        return dragState.mode === "start"
          ? snapRulerEndpointTo45Degrees(
              endpoint,
              dragState.startEnd,
              angleSnapTolerance,
              event.shiftKey,
            )
          : endpoint;
      })();
      const nextEnd = (() => {
        if (dragState.mode === "start") return dragState.startEnd.clone();
        const endpoint = dragState.startEnd.clone().add(delta);
        return dragState.mode === "end"
          ? snapRulerEndpointTo45Degrees(
              endpoint,
              dragState.startStart,
              angleSnapTolerance,
              event.shiftKey,
            )
          : endpoint;
      })();

      applyRulerUpdate(nextStart, nextEnd);
    },
    [
      applyRulerUpdate,
      editorStore,
      floorplannerStore,
      gl.domElement,
      onDragActiveChange,
      ruler.id,
    ],
  );

  React.useEffect(() => {
    const handleWindowPointerMove = (event: PointerEvent) => {
      if (
        !dragStateRef.current ||
        event.pointerId !== dragStateRef.current.pointerId
      ) {
        return;
      }
      moveDrag(
        event,
        getPointerPointOnFloorPlaneFromNative(event, camera, gl.domElement),
      );
    };
    const handleWindowPointerUp = (event: PointerEvent) => {
      finishDrag(event);
    };
    const handleWindowPointerCancel = (event: PointerEvent) => {
      finishDrag(event);
    };

    window.addEventListener("pointermove", handleWindowPointerMove, true);
    window.addEventListener("pointerup", handleWindowPointerUp, true);
    window.addEventListener("pointercancel", handleWindowPointerCancel, true);

    return () => {
      window.removeEventListener("pointermove", handleWindowPointerMove, true);
      window.removeEventListener("pointerup", handleWindowPointerUp, true);
      window.removeEventListener(
        "pointercancel",
        handleWindowPointerCancel,
        true,
      );
    };
  }, [camera, finishDrag, gl.domElement, moveDrag]);

  React.useEffect(
    () => () => {
      if (dragStateRef.current?.dragging) {
        floorplannerStore.cancelUndoTransaction();
        onDragActiveChange?.(false);
      }
      dragStateRef.current = null;
      editorStore.setRulerDragging(null);
      setMeasurementHoverCursor(gl.domElement, "default");
    },
    [editorStore, floorplannerStore, gl.domElement, onDragActiveChange],
  );

  const handlePointerDown = React.useCallback(
    (mode: Ruler3DDragMode, event: ThreeEvent<PointerEvent>) => {
      if (
        event.button !== 0 ||
        editorStore.panning ||
        editorStore.ruler3DConstructionMode
      )
        return;
      const startPoint = getPointerPointOnFloorPlane(event);
      if (!startPoint) return;

      stopOrbitControlEvent(event);
      setPointerCapture(event);
      selectRuler(event);

      dragStateRef.current = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
        pointerTarget: event.target as unknown as {
          releasePointerCapture?: (pointerId: number) => void;
        },
        mode,
        startPoint,
        startStart: ruler.start.clone(),
        startEnd: ruler.end.clone(),
        dragging: false,
      };
    },
    [
      editorStore.panning,
      editorStore.ruler3DConstructionMode,
      ruler.end,
      ruler.start,
      selectRuler,
    ],
  );

  if (!measurement) return null;

  return (
    <group>
      <Measurement3DObject measurement={measurement} selected={selected} />
      <RulerBodyHit3D
        start={measurement.points[0]}
        end={measurement.points[1]}
        onPointerDown={(event) => handlePointerDown("move", event)}
        onPointerEnter={(event) => {
          event.stopPropagation();
          if (!dragStateRef.current) {
            setMeasurementHoverCursor(gl.domElement, "grab");
          }
        }}
        onPointerLeave={(event) => {
          event.stopPropagation();
          if (!dragStateRef.current) {
            setMeasurementHoverCursor(gl.domElement, "default");
          }
        }}
      />
      {selected && (
        <>
          <RulerEndpointHandle3D
            position={measurement.points[0]}
            selected
            cursorElement={gl.domElement}
            onPointerDown={(event) => handlePointerDown("start", event)}
          />
          <RulerEndpointHandle3D
            position={measurement.points[1]}
            selected
            cursorElement={gl.domElement}
            onPointerDown={(event) => handlePointerDown("end", event)}
          />
        </>
      )}
    </group>
  );
});

const Ruler3DMeasurementObject: React.FC<{
  ruler: Ruler3DType;
  selected: boolean;
  onDragActiveChange?: (active: boolean) => void;
}> = observer(({ ruler, selected, onDragActiveChange }) => {
  const editorStore = React.useContext(editorStoreContext);
  const floorplannerStore = React.useContext(FloorplannerStoreContext);
  const { camera, gl, invalidate, scene } = useThree();
  const dragStateRef = React.useRef<FreeRuler3DDragState | null>(null);
  const measurement = createRuler3DMeasurement3D(ruler, {
    color: selected ? getRuler3DActiveColor(ruler) : getLineColor(ruler),
    lengthUnit: editorStore.displayLengthUnit,
  });
  if (measurement && selected) {
    measurement.label = `${measurement.label} · ${getRuler3DOrientationLabel(
      ruler,
    )}`;
  }

  /**
   * Endpoint placement while dragging: nearby features win, then the raw
   * object surface, and finally the drag plane with axis snapping applied.
   */
  const resolveDraggedEndpoint = React.useCallback(
    (
      event: ThreeEvent<PointerEvent> | PointerEvent,
      planePoint: THREE.Vector3,
      anchor: THREE.Vector3,
    ): THREE.Vector3 => {
      const placement = resolveRuler3DPlacement(
        event,
        camera,
        gl.domElement,
        scene,
      );
      if (event.shiftKey) {
        return lockRuler3DEndpointToWorldAxis(
          placement?.snapped ? placement.point : planePoint,
          anchor,
        );
      }
      if (placement?.snapped) return placement.point;
      return snapRuler3DEndpointToAxisDirections(planePoint, anchor);
    },
    [camera, gl.domElement, scene],
  );

  const resolveTranslatedRuler = React.useCallback(
    (
      start: THREE.Vector3,
      end: THREE.Vector3,
    ): { start: THREE.Vector3; end: THREE.Vector3 } => {
      const startPointer = getClientPointForWorldPoint(
        start,
        camera,
        gl.domElement,
      );
      const endPointer = getClientPointForWorldPoint(
        end,
        camera,
        gl.domElement,
      );
      const startPlacement = startPointer
        ? resolveRuler3DPlacement(startPointer, camera, gl.domElement, scene)
        : null;
      const endPlacement = endPointer
        ? resolveRuler3DPlacement(endPointer, camera, gl.domElement, scene)
        : null;

      return snapRuler3DTranslation(start, end, startPlacement, endPlacement);
    },
    [camera, gl.domElement, scene],
  );

  const selectRuler = React.useCallback(
    (event: ThreeEvent<PointerEvent> | PointerEvent) => {
      if (
        !event.shiftKey &&
        editorStore.selections.find(
          (selection) => selection.id === ruler.id,
        ) === undefined
      ) {
        editorStore.clearSelections();
      }
      floorplannerStore.selectRuler3D(ruler.id);
      editorStore.addSelection(ruler);
      invalidate();
    },
    [editorStore, floorplannerStore, invalidate, ruler],
  );

  const applyRulerUpdate = React.useCallback(
    (start: THREE.Vector3, end: THREE.Vector3) => {
      const latestRuler = floorplannerStore.rulers3DMap.get(ruler.id);
      if (!latestRuler) return;
      floorplannerStore.updateRuler3D({
        ...latestRuler,
        start,
        end,
      });
      invalidate();
    },
    [floorplannerStore, invalidate, ruler.id],
  );

  const finishDrag = React.useCallback(
    (event?: ThreeEvent<PointerEvent> | PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      if (event && event.pointerId !== dragState.pointerId) return;

      stopOrbitControlEvent(event);
      releasePointerCapture(dragState);
      dragStateRef.current = null;
      editorStore.setRulerDragging(null);
      setMeasurementHoverCursor(gl.domElement, "default");

      if (dragState.dragging) {
        const latestRuler = floorplannerStore.rulers3DMap.get(ruler.id);
        const moved =
          latestRuler &&
          (latestRuler.start.distanceToSquared(dragState.startStart) > 1e-10 ||
            latestRuler.end.distanceToSquared(dragState.startEnd) > 1e-10);

        if (moved) {
          floorplannerStore.recordRuler3DChange(ruler.id, "modify");
          floorplannerStore.pushToUndoStack();
          floorplannerStore.setDirty();
        } else {
          floorplannerStore.cancelUndoTransaction();
        }
        onDragActiveChange?.(false);
      }
      invalidate();
    },
    [
      editorStore,
      floorplannerStore,
      gl.domElement,
      invalidate,
      onDragActiveChange,
      ruler.id,
    ],
  );

  const moveDrag = React.useCallback(
    (
      event: ThreeEvent<PointerEvent> | PointerEvent,
      currentPoint: THREE.Vector3 | null,
    ) => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) return;

      stopOrbitControlEvent(event);

      if (!dragState.dragging) {
        const movedFarEnough =
          Math.abs(event.clientX - dragState.clientX) >
            DRAG_MOVE_THRESHOLD_PX ||
          Math.abs(event.clientY - dragState.clientY) > DRAG_MOVE_THRESHOLD_PX;

        if (!movedFarEnough) return;

        dragState.dragging = true;
        setMeasurementHoverCursor(
          gl.domElement,
          dragState.mode === "move" ? "grabbing" : "crosshair",
        );
        editorStore.setRulerDragging(ruler.id);
        floorplannerStore.beginUndoTransaction(
          dragState.mode === "move" ? "drag 3D ruler" : "resize 3D ruler",
        );
        floorplannerStore.recordRuler3DForUndo(ruler.id);
        onDragActiveChange?.(true);
      }

      if (!currentPoint) return;

      const delta = new THREE.Vector3().subVectors(
        currentPoint,
        dragState.startPoint,
      );

      if (dragState.mode === "start") {
        const endpoint = dragState.startStart.clone().add(delta);
        const nextStart = resolveDraggedEndpoint(
          event,
          endpoint,
          dragState.startEnd,
        );
        applyRulerUpdate(nextStart, dragState.startEnd.clone());
        return;
      }

      if (dragState.mode === "end") {
        const endpoint = dragState.startEnd.clone().add(delta);
        const nextEnd = resolveDraggedEndpoint(
          event,
          endpoint,
          dragState.startStart,
        );
        applyRulerUpdate(dragState.startStart.clone(), nextEnd);
        return;
      }

      let nextStart = new THREE.Vector3(
        dragState.startStart.x + delta.x,
        dragState.startStart.y + delta.y,
        dragState.startStart.z + delta.z,
      );
      let nextEnd = new THREE.Vector3(
        dragState.startEnd.x + delta.x,
        dragState.startEnd.y + delta.y,
        dragState.startEnd.z + delta.z,
      );

      if (!event.shiftKey) {
        const snappedRuler = resolveTranslatedRuler(nextStart, nextEnd);
        nextStart = snappedRuler.start;
        nextEnd = snappedRuler.end;
      }
      applyRulerUpdate(nextStart, nextEnd);
    },
    [
      applyRulerUpdate,
      editorStore,
      floorplannerStore,
      gl.domElement,
      onDragActiveChange,
      resolveDraggedEndpoint,
      resolveTranslatedRuler,
      ruler.id,
    ],
  );

  React.useEffect(() => {
    const handleWindowPointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }
      moveDrag(
        event,
        getPointerPointOnPlaneFromNative(
          event,
          camera,
          gl.domElement,
          dragState.dragPlane,
        ),
      );
    };
    const handleWindowPointerUp = (event: PointerEvent) => {
      finishDrag(event);
    };
    const handleWindowPointerCancel = (event: PointerEvent) => {
      finishDrag(event);
    };

    window.addEventListener("pointermove", handleWindowPointerMove, true);
    window.addEventListener("pointerup", handleWindowPointerUp, true);
    window.addEventListener("pointercancel", handleWindowPointerCancel, true);

    return () => {
      window.removeEventListener("pointermove", handleWindowPointerMove, true);
      window.removeEventListener("pointerup", handleWindowPointerUp, true);
      window.removeEventListener(
        "pointercancel",
        handleWindowPointerCancel,
        true,
      );
    };
  }, [camera, finishDrag, gl.domElement, moveDrag]);

  React.useEffect(
    () => () => {
      if (dragStateRef.current?.dragging) {
        floorplannerStore.cancelUndoTransaction();
        onDragActiveChange?.(false);
      }
      dragStateRef.current = null;
      editorStore.setRulerDragging(null);
      setMeasurementHoverCursor(gl.domElement, "default");
    },
    [editorStore, floorplannerStore, gl.domElement, onDragActiveChange],
  );

  const handlePointerDown = React.useCallback(
    (mode: FreeRuler3DDragMode, event: ThreeEvent<PointerEvent>) => {
      if (
        event.button !== 0 ||
        editorStore.panning ||
        editorStore.ruler3DConstructionMode
      )
        return;

      const anchor =
        mode === "start"
          ? ruler.start
          : mode === "end"
            ? ruler.end
            : new THREE.Vector3()
                .addVectors(ruler.start, ruler.end)
                .multiplyScalar(0.5);
      const dragPlane = createCameraFacingPlane(camera, anchor);
      const startPoint = getPointerPointOnPlane(event, dragPlane);
      if (!startPoint) return;

      stopOrbitControlEvent(event);
      setPointerCapture(event);
      selectRuler(event);

      dragStateRef.current = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
        pointerTarget: event.target as unknown as {
          releasePointerCapture?: (pointerId: number) => void;
        },
        mode,
        dragPlane,
        startPoint,
        startStart: ruler.start.clone(),
        startEnd: ruler.end.clone(),
        dragging: false,
      };
    },
    [
      camera,
      editorStore.panning,
      editorStore.ruler3DConstructionMode,
      ruler.end,
      ruler.start,
      selectRuler,
    ],
  );

  if (!measurement) return null;

  const start = measurement.points[0];
  const end = measurement.points[1];
  return (
    <group>
      <Measurement3DObject measurement={measurement} selected={selected} />
      <RulerBodyHit3D
        start={start}
        end={end}
        onPointerDown={(event) => handlePointerDown("move", event)}
        onPointerEnter={(event) => {
          event.stopPropagation();
          if (!dragStateRef.current) {
            setMeasurementHoverCursor(gl.domElement, "grab");
          }
        }}
        onPointerLeave={(event) => {
          event.stopPropagation();
          if (!dragStateRef.current) {
            setMeasurementHoverCursor(gl.domElement, "default");
          }
        }}
      />
      {selected && (
        <>
          <RulerEndpointHandle3D
            position={start}
            selected
            color={measurement.color}
            cursorElement={gl.domElement}
            onPointerDown={(event) => handlePointerDown("start", event)}
          />
          <RulerEndpointHandle3D
            position={end}
            selected
            color={measurement.color}
            cursorElement={gl.domElement}
            onPointerDown={(event) => handlePointerDown("end", event)}
          />
        </>
      )}
    </group>
  );
});

const RulerEndpointHandle3D: React.FC<{
  position: THREE.Vector3;
  selected: boolean;
  color?: THREE.ColorRepresentation;
  cursorElement: HTMLElement;
  onPointerDown: (event: ThreeEvent<PointerEvent>) => void;
}> = ({ position, selected, color, cursorElement, onPointerDown }) => {
  const { camera, size } = useThree();
  const handleRef = React.useRef<THREE.Group>(null);
  const initialScale =
    getWorldUnitsPerPixelAtPoint(camera, position, size.height) ??
    RULER_HANDLE_FALLBACK_WORLD_UNITS_PER_PIXEL;

  useFrame(({ camera: frameCamera, size: frameSize }) => {
    const handle = handleRef.current;
    if (!handle) return;
    const scale = getWorldUnitsPerPixelAtPoint(
      frameCamera,
      position,
      frameSize.height,
    );
    if (scale && Math.abs(handle.scale.x - scale) > 1e-5) {
      handle.scale.setScalar(scale);
    }
  });

  return (
    <group
      ref={handleRef}
      position={[position.x, position.y, position.z]}
      scale={initialScale}
      onPointerDown={(event) => {
        event.stopPropagation();
        onPointerDown(event);
      }}
      onPointerEnter={(event) => {
        event.stopPropagation();
        setMeasurementHoverCursor(cursorElement, "crosshair");
      }}
      onPointerLeave={(event) => {
        event.stopPropagation();
        setMeasurementHoverCursor(cursorElement, "default");
      }}
    >
      <mesh renderOrder={1002} raycast={rulerHandlePickRaycast}>
        <sphereGeometry args={[RULER_HANDLE_HIT_RADIUS_PX, 16, 16]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      <mesh renderOrder={1003}>
        <sphereGeometry args={[RULER_HANDLE_RADIUS_PX, 18, 18]} />
        <meshBasicMaterial
          color={color ?? (selected ? CANVAS_ACCENT_COLOR_HEX : 0xffffff)}
          depthTest={false}
        />
      </mesh>
    </group>
  );
};

const RulerBodyHit3D: React.FC<{
  start: THREE.Vector3;
  end: THREE.Vector3;
  onPointerDown: (event: ThreeEvent<PointerEvent>) => void;
  onPointerEnter: (event: ThreeEvent<PointerEvent>) => void;
  onPointerLeave: (event: ThreeEvent<PointerEvent>) => void;
}> = ({ start, end, onPointerDown, onPointerEnter, onPointerLeave }) => {
  const { camera, size } = useThree();
  const hitRef = React.useRef<THREE.Mesh>(null);
  const length = start.distanceTo(end);
  const center = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  const direction = new THREE.Vector3().subVectors(end, start);
  const quaternion =
    direction.length() > 1e-6
      ? new THREE.Quaternion().setFromUnitVectors(
          new THREE.Vector3(0, 1, 0),
          direction.normalize(),
        )
      : new THREE.Quaternion();
  const initialRadius = getRulerBodyHitRadius(camera, center, size.height);

  useFrame(({ camera: frameCamera, size: frameSize }) => {
    const hitTarget = hitRef.current;
    if (!hitTarget) return;
    const radius = getRulerBodyHitRadius(frameCamera, center, frameSize.height);
    if (Math.abs(hitTarget.scale.x - radius) > 1e-5) {
      hitTarget.scale.x = radius;
      hitTarget.scale.z = radius;
    }
  });

  if (length <= 1e-6) return null;

  return (
    <mesh
      ref={hitRef}
      position={center}
      quaternion={quaternion}
      scale={[initialRadius, 1, initialRadius]}
      renderOrder={1001}
      raycast={rulerPickRaycast}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      <cylinderGeometry args={[1, 1, length, 12]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
};

function getLineColor(
  ruler: RulerLineType | Ruler3DType,
): THREE.ColorRepresentation {
  if (ruler.selected) return ruler.hoverColor ?? CANVAS_ACCENT_COLOR;
  return ruler.lineColor ?? "black";
}

function getPointerPointOnFloorPlane(
  event: ThreeEvent<PointerEvent>,
): THREE.Vector3 | null {
  return getPointerPointOnPlane(event, FLOOR_PLANE);
}

function getPointerPointOnPlane(
  event: ThreeEvent<PointerEvent>,
  plane: THREE.Plane,
): THREE.Vector3 | null {
  return event.ray.intersectPlane(plane, new THREE.Vector3());
}

function getPointerPointOnFloorPlaneFromNative(
  event: PointerEvent,
  camera: THREE.Camera,
  element: HTMLElement,
): THREE.Vector3 | null {
  return getPointerPointOnPlaneFromNative(event, camera, element, FLOOR_PLANE);
}

function getClientPointForWorldPoint(
  point: THREE.Vector3,
  camera: THREE.Camera,
  element: HTMLElement,
): { clientX: number; clientY: number } | null {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;

  const projected = point.clone().project(camera);
  if (
    !Number.isFinite(projected.x) ||
    !Number.isFinite(projected.y) ||
    !Number.isFinite(projected.z) ||
    projected.z < -1 ||
    projected.z > 1
  ) {
    return null;
  }

  return {
    clientX: rect.left + ((projected.x + 1) / 2) * rect.width,
    clientY: rect.top + ((1 - projected.y) / 2) * rect.height,
  };
}

function getPointerPointOnPlaneFromNative(
  event: PointerEvent,
  camera: THREE.Camera,
  element: HTMLElement,
  plane: THREE.Plane,
): THREE.Vector3 | null {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;

  const pointer = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1,
  );
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(pointer, camera);
  return raycaster.ray.intersectPlane(plane, new THREE.Vector3());
}

function createCameraFacingPlane(
  camera: THREE.Camera,
  anchor: THREE.Vector3,
): THREE.Plane {
  const cameraDirection = new THREE.Vector3();
  camera.getWorldDirection(cameraDirection);
  const normal = cameraDirection.clone();
  if (normal.lengthSq() < 1e-8) {
    normal.set(0, 0, 1);
  } else {
    normal.normalize();
  }
  return new THREE.Plane().setFromNormalAndCoplanarPoint(normal, anchor);
}

function getRulerBodyHitRadius(
  camera: THREE.Camera,
  point: THREE.Vector3,
  viewportHeightPx: number,
): number {
  const worldUnitsPerPixel = getWorldUnitsPerPixelAtPoint(
    camera,
    point,
    viewportHeightPx,
  );
  return worldUnitsPerPixel
    ? worldUnitsPerPixel * RULER_BODY_HIT_RADIUS_PX
    : RULER_BODY_HIT_FALLBACK_RADIUS;
}

function setPointerCapture(event: ThreeEvent<PointerEvent>) {
  try {
    const pointerTarget = event.target as unknown as {
      setPointerCapture?: (pointerId: number) => void;
    };
    pointerTarget.setPointerCapture?.(event.pointerId);
  } catch {
    // Pointer capture can fail for synthetic or already-captured events.
  }
}

function releasePointerCapture(dragState: {
  pointerId: number;
  pointerTarget?: {
    releasePointerCapture?: (pointerId: number) => void;
  };
}) {
  try {
    dragState.pointerTarget?.releasePointerCapture?.(dragState.pointerId);
  } catch {
    // Pointer capture can already be released by the browser.
  }
}

function stopOrbitControlEvent(
  event?: ThreeEvent<PointerEvent> | PointerEvent,
) {
  if (!event) return;
  event.stopPropagation();
  if ("nativeEvent" in event) {
    if (event.nativeEvent.cancelable) {
      event.nativeEvent.preventDefault();
    }
    event.nativeEvent.stopImmediatePropagation?.();
    return;
  }
  if (event.cancelable) {
    event.preventDefault();
  }
  event.stopImmediatePropagation?.();
}

export default Measurement3DLayer;
